interface Endpoints {
  destinations: string;
  timeSlots: string;
  customerAccount: string;
  createAppointment: string;
  modifyAppointment: string;
  getAppointmentInformation: string;
  createEmailLead: string;
  relevanceSearch: string;
  branches: string;
  nearbyAgents: string;
  locations: string;
}

export const endpointPaths: Endpoints = {
  destinations: '/travel-scheduler/scheduler/v1/destinations',
  timeSlots: '/travel-scheduler/scheduler/v1/agents-and-timeslots/{workTypeId}',
  nearbyAgents: '/travel-scheduler/scheduler/v1/nearby-agents/{workTypeId}',
  customerAccount: '/travel-scheduler/scheduler/v1/customer-account',
  modifyAppointment:
    '/travel-scheduler/scheduler/v1/modify-appointment/{appointmentId}',
  createAppointment: '/travel-scheduler/scheduler/v1/appointment',
  getAppointmentInformation:
    '/travel-scheduler/scheduler/v1/appointment/{appointmentId}',
  createEmailLead: '/travel-scheduler/scheduler/v1/process-email-inquiry',
  relevanceSearch: '/travel-relevance-search/relevance-search/v1/search',
  branches: '/travel-scheduler/scheduler/v1/branches',
  locations: '/travel-advisor/advisor/v1/locations',
};

export enum Environments {
  test = 'mockapi',
  development = 'mockapi',
  dev = 'dev',
  qa = 'qa',
  uat = 'uat',
  uat2 = 'uat2',
  prod = 'prod',
}

export const prodEnvironments: Array<Environments> = [Environments.prod];

/**
 * Maps each environment to its short API name (matches OLB pattern).
 * UAT1 and UAT2 both map to 'uat' so they share the same API gateway.
 * The backend then routes to the correct Salesforce sandbox based on
 * the request's Origin header.
 */
type ShortEnvironment = 'mockapi' | 'dev' | 'qa' | 'uat' | 'prod';
const environmentShortNameMap: Record<string, ShortEnvironment> = {
  mockapi: 'mockapi',
  dev: 'dev',
  qa: 'qa',
  uat: 'uat',
  uat2: 'uat',
  prod: 'prod',
};

export const getApiDomain = (env: Environments): string => {
  const shortName = environmentShortNameMap[env] ?? env;
  switch (shortName) {
    case 'mockapi':
      return 'mockapi';
    case 'prod':
      return 'common.api.ace.aaa.com';
    default:
      return `common.api.${shortName}.ace.aaa.com`;
  }
};

export const isEnvironmentProd = (hostname: string): boolean => {
  return prodEnvironments.includes(getEnvironmentFromHost(hostname));
};

export const getEnvironmentFromHost = (hostname: string): Environments => {
  // Check for ACE Identity mode
  if (import.meta.env.VITE_IDENTITY_MODE === 'true') {
    return Environments.development;
  }

  if (
    hostname.search('localhost') > -1 &&
    (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test')
  ) {
    return Environments.development;
  }

  if (
    hostname.search('localhost') > -1 &&
    import.meta.env.VITE_REMOTE_UAT2 === 'true'
  ) {
    return Environments.uat2;
  }

  if (hostname.search('localhost') > -1) {
    return Environments.dev;
  }

  if (hostname.startsWith('appdev1') || hostname.startsWith('appdev2')) {
    return Environments.dev;
  }

  if (hostname.startsWith('appqa1') || hostname.startsWith('appqa2')) {
    return Environments.qa;
  }

  if (hostname.startsWith('appuat1')) {
    return Environments.uat;
  }

  if (hostname.startsWith('appuat2')) {
    return Environments.uat2;
  }

  if (hostname.startsWith('app.ace')) {
    return Environments.prod;
  }

  return Environments.uat;
};

const usesLocalUat2Proxy = (hostname: string): boolean =>
  import.meta.env.VITE_REMOTE_UAT2 === 'true' &&
  hostname.search('localhost') > -1;

const generateEndpoints = (hostname: string): Endpoints => {
  const env = getEnvironmentFromHost(hostname);

  const isAceIdentityEnv = import.meta.env.VITE_IDENTITY_MODE === 'true';
  const useLocalUat2Proxy = usesLocalUat2Proxy(hostname);

  const protocol: string = isAceIdentityEnv
    ? 'https'
    : ['development', 'test'].includes(process.env.NODE_ENV || 'development')
      ? 'http'
      : 'https';

  const apiBase = useLocalUat2Proxy
    ? window.location.origin
    : `${protocol}://${getApiDomain(env)}`;

  const _endpoints: Endpoints = {
    destinations: `${apiBase}${endpointPaths.destinations}`,
    timeSlots: `${apiBase}${endpointPaths.timeSlots}`,
    nearbyAgents: `${apiBase}${endpointPaths.nearbyAgents}`,
    customerAccount: `${apiBase}${endpointPaths.customerAccount}`,
    createAppointment: `${apiBase}${endpointPaths.createAppointment}`,
    modifyAppointment: `${apiBase}${endpointPaths.modifyAppointment}`,
    getAppointmentInformation: `${apiBase}${endpointPaths.getAppointmentInformation}`,
    createEmailLead: `${apiBase}${endpointPaths.createEmailLead}`,
    relevanceSearch: `${apiBase}${endpointPaths.relevanceSearch}`,
    branches: `${apiBase}${endpointPaths.branches}`,
    locations: `${apiBase}${endpointPaths.locations}`,
  };
  return _endpoints;
};

export const getEndpoints = () => generateEndpoints(window.location.hostname);
